/**
* ------------------------------------------------------------------------
* Public API
* ------------------------------------------------------------------------
*/

import path from 'path';
import findItems, { findItemsForBuild, loadItem, findItemFiles } from './lib/find-items';
import mergeItems from './lib/merge-items';
import { isExcluded } from './lib/excludes';

/**
 * Finds CXP Items And Builds Sources
 * @module @bb-cli/lib-find-backbase-items
 */

/**
 * Find unbuilt items, and prebuilt items.
 *
 * @memberof module:@bb-cli/lib-find-backbase-items
 * @param {Array.<string>} unbuiltPaths Paths to unbuilt sources
 * @param {Array.<string>} prebuiltPaths Array of paths to pre-built sources
 * @param {Array.<string>} exclude Array of paths to exclude from packaging
 * @return {Promise.<FoundItems>} Array of items found
 * @example
 *
 * ``` javascript
 * import { findUnbuiltAndPrebuiltItems } from '@bb-cli/lib-find-backbase-items';
 * findAndBuild(['.'], ['node_modules'], ['target'])
 *   .then(({ unbuiltItems, prebuiltItems }) =>
 *     console.log(`${unbuiltItems.length} items to build`));
 * ```
 */
const findUnbuiltAndPrebuiltItems = (unbuiltPaths, prebuiltPaths, exclude) => {
  const builtPathsResolved = prebuiltPaths.map(p => path.resolve(p));
  return Promise.all([
    findItemsForBuild(unbuiltPaths, exclude.concat(builtPathsResolved)),
    findItems(builtPathsResolved, exclude), // find in pre-built paths
  ])
  .then(([unbuiltItems, prebuiltItems]) => ({ unbuiltItems, prebuiltItems }));
};

export {
  loadItem,
  findItems,
  findItemsForBuild,
  findUnbuiltAndPrebuiltItems,
  findItemFiles,
  mergeItems,
  isExcluded,
};

/**
 * An object that describes a catalog item in CXP Manager.
 *
 * @typedef {Object} CXPItem
 * @property {string} name The CXP item name.
 * @property {string} type The CXP item type.
 * @property {?string} version The CXP item version.
 * @property {string} srcDir The path to the item's source on the file system.
 * @property {?string} distDir The path to the built code on the file system, if it was built.
 * @property {?Array.<string>} files The files for this item
 */

/**
 * An object that describes found CXP items.
 *
 * @typedef {Object} FoundItems
 * @property {CXPItem[]} unbuiltItems
 * @property {CXPItem[]} prebuiltItems
 */
